/**
 * Ver de cerca las láminas de contenido enriquecido (Optioutlet).
 *
 * El arte de "Descubre más..." se diseñó para escritorio: la tira mide
 * 1000px --1200 en la del graduado-- y el texto de cuerpo tiene unos 11px
 * de altura de mayúscula. En un teléfono de 390px la lámina entra a 390 y
 * esas mayúsculas quedan en **3.6px**. No es que se lean mal: no se ven.
 *
 * No hay arreglo de maquetación posible: para que ese texto llegue a un
 * tamaño cómodo la lámina tendría que medir más del doble que la pantalla.
 * Lo que sí se puede es dejar verla de cerca, que es lo que hace este
 * guion: al tocar una lámina abre las cinco a su tamaño real, pegadas
 * igual que en la tira, y deja recorrerlas con el dedo.
 *
 * **Esto es un remedio, no la solución.** La solución es arte vertical
 * pensado para teléfono. Mientras llegue, el contenido se puede leer.
 *
 * Sólo se monta hasta 999px de ancho. De ahí para arriba la tira ya se ve
 * entera y tocar una imagen para que se abra otra cosa sería una sorpresa.
 */

const TIRA = '[id$="__detalles"] .details-content .image-block';
const CORTE = '(max-width: 999px)';

let visor = null;
let scrollBloqueado = null;

function bloquearFondo() {
  // Arriba de 990px el que hace scroll es .page-wrapper y no el documento
  // --ver [[horizon-css-gotchas]]--, pero este visor es sólo de teléfono,
  // así que basta el documento. Se guarda lo que había por si el tema ya
  // traía algo puesto.
  scrollBloqueado = document.documentElement.style.overflow;
  document.documentElement.style.overflow = 'hidden';
}

function soltarFondo() {
  document.documentElement.style.overflow = scrollBloqueado ?? '';
  scrollBloqueado = null;
}

function cerrar() {
  if (!visor) return;
  visor.remove();
  visor = null;
  soltarFondo();
  document.removeEventListener('keydown', alTeclado);
}

function alTeclado(evento) {
  if (evento.key === 'Escape') cerrar();
}

function abrir(laminas, indice) {
  if (visor) return;

  visor = document.createElement('div');
  visor.className = 'ol-lamina-visor';
  visor.setAttribute('role', 'dialog');
  visor.setAttribute('aria-modal', 'true');
  visor.setAttribute('aria-label', 'Contenido ampliado');

  const cerrarBoton = document.createElement('button');
  cerrarBoton.type = 'button';
  cerrarBoton.className = 'ol-lamina-visor__cerrar';
  cerrarBoton.setAttribute('aria-label', 'Cerrar');
  cerrarBoton.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="m5 5 14 14M19 5 5 19"/></svg>';
  cerrarBoton.addEventListener('click', cerrar);

  const lienzo = document.createElement('div');
  lienzo.className = 'ol-lamina-visor__lienzo';

  // El ancho real lo manda la propia imagen: 1000 en el catálogo, 1200 en
  // el graduado. Pedirle un número fijo dejaría una de las dos mal.
  let ancho = 0;
  const copias = laminas.map((original) => {
    const img = document.createElement('img');
    img.src = original.currentSrc || original.src;
    img.alt = original.alt || '';
    img.decoding = 'async';
    ancho = Math.max(ancho, original.naturalWidth || 0);
    lienzo.appendChild(img);
    return img;
  });

  if (ancho > 0) lienzo.style.setProperty('--ol-lamina-ancho', `${ancho}px`);

  visor.append(cerrarBoton, lienzo);
  document.body.appendChild(visor);
  bloquearFondo();
  document.addEventListener('keydown', alTeclado);

  // Se abre en la lámina que tocaron y a media anchura, que es donde suele
  // estar el texto; si entrara por la orilla izquierda parecería cortada.
  requestAnimationFrame(() => {
    const destino = copias[indice];
    if (destino) lienzo.scrollTop = destino.offsetTop;
    lienzo.scrollLeft = (lienzo.scrollWidth - lienzo.clientWidth) / 2;
  });

  cerrarBoton.focus();
}

function montar() {
  if (!window.matchMedia(CORTE).matches) return;

  const bloques = [...document.querySelectorAll(TIRA)];
  if (bloques.length === 0) return;

  const laminas = bloques
    .map((bloque) => bloque.querySelector('img'))
    .filter(Boolean);
  if (laminas.length === 0) return;

  bloques.forEach((bloque, indice) => {
    if (bloque.dataset.olAmpliable === 'si') return;
    bloque.dataset.olAmpliable = 'si';
    bloque.setAttribute('role', 'button');
    bloque.setAttribute('tabindex', '0');
    bloque.setAttribute('aria-label', 'Ver el contenido de cerca');

    bloque.addEventListener('click', () => abrir(laminas, indice));
    bloque.addEventListener('keydown', (evento) => {
      if (evento.key === 'Enter' || evento.key === ' ') {
        evento.preventDefault();
        abrir(laminas, indice);
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', montar, { once: true });
} else {
  montar();
}

// El editor de temas vuelve a pintar la sección al tocarle un ajuste.
document.addEventListener('shopify:section:load', montar);
